import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Chip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SCUIContent } from '../../atomic/organisms';
import { SCUICustomDataGrid, SCUIGeneralDrawer, SCUIBreadCrumb } from '../../atomic/molecules';
import { SCUIButton, SCUILoadingLinear, SCUITextField } from '../../atomic/atoms';
import RpaQuery from '../../services/query/rpa/RpaQuery.services';
import TimeHelper from '../../utils/time';
import { RpaRequestDetailDrawer, RpaRequestSummary } from './components/RpaRequestDetailDrawer';

const PAGE_SIZE = 20;

const extractList = (res: any): any[] => {
    if (!res) return [];
    return res.model?.data ?? res.data ?? res.model ?? [];
};

const mapRequestRow = (item: any): RpaRequestSummary & { id: number } => ({
    id: item.rpaRequestId ?? item.RPARequestId,
    rpaRequestId: item.rpaRequestId ?? item.RPARequestId,
    userName: item.userName ?? item.UserName ?? '',
    createDate: item.createDate ?? item.CreateDate ?? '',
    statusKey: item.statusKey ?? item.StatusKey ?? '',
});

const statusChip = (statusKey: string, t: (key: string) => string) => {
    if (statusKey === 'Completed') {
        return (
            <Chip
                label={t('rpaResender.rpaRequestList.completed')}
                size="small"
                sx={{ backgroundColor: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }}
            />
        );
    }
    if (statusKey === 'Failed') {
        return (
            <Chip
                label={t('rpaRequestManagement.status.failed')}
                size="small"
                sx={{ backgroundColor: '#ffebee', color: '#c62828', fontWeight: 600 }}
            />
        );
    }
    if (statusKey === 'InProgress') {
        return (
            <Chip
                label={t('rpaRequestManagement.status.inProgress')}
                size="small"
                sx={{ backgroundColor: '#e3f2fd', color: '#1565c0', fontWeight: 600 }}
            />
        );
    }
    if (statusKey === 'WaitingProcess' || statusKey === 'Pending') {
        return (
            <Chip
                label={t('rpaResender.rpaRequestList.waitingProcess')}
                size="small"
                sx={{ backgroundColor: '#fff3e0', color: '#e65100', fontWeight: 600 }}
            />
        );
    }
    return <Chip label={statusKey} size="small" />;
};

export const RpaRequestManagement: FC = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<(RpaRequestSummary & { id: number })[]>([]);
    const [page, setPage] = useState(0);
    const [totalRowCount, setTotalRowCount] = useState(0);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState<RpaRequestSummary | null>(null);
    const [searchKeyword, setSearchKeyword] = useState('');

    const [pageSize, setPageSize] = useState(PAGE_SIZE);

    const loadRequests = useCallback((keyword?: string, pageIndex: number = 0, size: number = pageSize) => {
        setLoading(true);
        RpaQuery.RpaRequestList({ 
            take: size, 
            skip: pageIndex * size, 
            searchKeyword: keyword !== undefined ? keyword : searchKeyword 
        })
            .then((res: any) => {
                const mappedRows = extractList(res).map(mapRequestRow);
                setRows(mappedRows);
                
                // Backend'den gelen TotalCount değerini kullan
                const total = res?.model?.totalCount ?? res?.data?.totalCount ?? res?.totalCount ?? 
                             (pageIndex * size + mappedRows.length + (mappedRows.length === size ? 1 : 0));
                setTotalRowCount(total);
            })
            .catch((error: unknown) => {
                console.error('RpaRequestList error:', error);
                setRows([]);
                setTotalRowCount(0);
            })
            .finally(() => setLoading(false));
    }, [searchKeyword, pageSize]);

    useEffect(() => {
        loadRequests('', 0, PAGE_SIZE);
    }, []);

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
        loadRequests(searchKeyword, newPage, pageSize);
    };

    const handlePageSizeChange = (newPageSize: number) => {
        if (newPageSize !== pageSize) {
            setPageSize(newPageSize);
            setPage(0); // Sayfa boyutu değiştiğinde ilk sayfaya dön
            loadRequests(searchKeyword, 0, newPageSize);
        }
    };

    const openDetail = (row: RpaRequestSummary & { id: number }) => {
        setSelectedRequest(row);
        setDrawerOpen(true);
    };

    const columns = useMemo(
        () => [
            {
                field: 'rpaRequestId',
                headerName: t('rpaRequestManagement.columns.requestId'),
                flex: 0.6,
                minWidth: 100,
            },
            {
                field: 'createDate',
                headerName: t('rpaRequestManagement.columns.createDate'),
                flex: 1,
                minWidth: 160,
                valueGetter: (_: unknown, row: RpaRequestSummary) =>
                    row.createDate ? TimeHelper.formatDateHour(row.createDate) : '',
            },
            {
                field: 'userName',
                headerName: t('rpaRequestManagement.columns.userName'),
                flex: 1,
                minWidth: 140,
            },
            {
                field: 'statusKey',
                headerName: t('rpaRequestManagement.columns.status'),
                flex: 1,
                minWidth: 140,
                renderCell: (params: any) => statusChip(params.row.statusKey, t),
            },
            {
                field: 'detail',
                headerName: t('general.detail'),
                flex: 0.8,
                minWidth: 120,
                sortable: false,
                align: 'center',
                headerAlign: 'center',
                renderCell: (params: any) => (
                    <SCUIButton
                        text={t('rpaRequestManagement.viewDetails')}
                        type="primary"
                        sizeType="fixed"
                        size="m"
                        onClick={() => openDetail(params.row)}
                    />
                ),
            },
        ],
        [t]
    );

    return (
        <SCUIContent className="rpa-request-management">
            <Box className="rpa-request-management__breadcrumb">
                <SCUIBreadCrumb
                    title={t('rpaRequestManagement.pageTitle')}
                    componentLink="RpaRequestManagement"
                />
            </Box>
            <Box 
                sx={{ display: 'flex', gap: 2, mt: 2, mb: 2, alignItems: 'center' }}
                onKeyDown={(e: any) => {
                    if (e.key === 'Enter') {
                        setPage(0);
                        loadRequests(searchKeyword, 0, pageSize);
                    }
                }}
            >
                <SCUITextField
                    label={t('rpaRequestManagement.searchLabel', 'Sevkiyat No Ara')}
                    value={searchKeyword}
                    onChange={(val: string) => setSearchKeyword(val)}
                    stateName="searchKeyword"
                    sx={{ width: 300 }}
                    size="m"
                />
                <SCUIButton
                    text={t('general.search', 'Ara')}
                    type="primary"
                    sizeType="fixed"
                    size="m"
                    onClick={() => {
                        setPage(0);
                        loadRequests(searchKeyword, 0, pageSize);
                    }}
                />
            </Box>
            <Box className="rpa-request-management__loading-wrap">
                <SCUILoadingLinear display={loading} />
            </Box>
            <Box className="rpa-request-management__grid-wrap" sx={{ mt: 2, height: 640, width: '100%' }}>
                <SCUICustomDataGrid
                    randomId
                    rows={rows}
                    columns={columns}
                    page={page}
                    pageSize={pageSize}
                    onPageChange={(i: number) => handlePageChange(i)}
                    onPageSizeChange={(i: number) => handlePageSizeChange(i)}
                    rowCount={totalRowCount}
                    loading={loading}
                    paginationModeChange={true}
                    rowsPerPageOptions={[10, 20, 50, 100]}
                />
            </Box>
            <SCUIGeneralDrawer
                open={drawerOpen}
                anchor="right"
                width="56rem"
                onClose={(_: unknown, reason: string) => {
                    if (reason === 'backdropClick') {
                        setDrawerOpen(false);
                    }
                }}
            >
                <RpaRequestDetailDrawer request={selectedRequest} />
            </SCUIGeneralDrawer>
        </SCUIContent>
    );
};
